import {
  all,
  call,
  fork,
  spawn,
  getContext,
  put,
  take,
} from 'redux-saga/effects';
import { scrapeSpoofId } from 'utils/url';
import {
  SPOOF_REQUESTED,
  SPOOF_STARTED,
  SPOOF_STOPPED,
  SPOOF_CANCELLED,
  SPOOF_ERRORED,
  requestSpoof,
  startSpoof,
  stopSpoof,
  errorSpoof,
} from 'actions/spoofing';
import { showToast } from 'actions/toaster';
import { reportError } from 'helpers/error-reporting';

/**
 * begin a new spoofing session
 *
 * @param {string} id - the id of the account to spoof into
 */
export function* spoof(id) {
  try {
    const auth = yield getContext('auth');
    yield call(auth.spoof, id);
    yield put(startSpoof(id));
  } catch (error) {
    yield all([
      call(reportError, error),
      put(errorSpoof(error)),
      put(
        showToast({
          type: 'error',
          message: 'Failed to spoof into account',
        })
      ),
    ]);
  }
}

/**
 * cancel an existing spoofing session
 */
export function* despoof() {
  try {
    const auth = yield getContext('auth');
    yield call(auth.despoof);
    yield put(stopSpoof());
  } catch (error) {
    yield call(reportError, error);
  }
}

/**
 * handle the startup cases:
 *   - already spoofing
 *   - already spoofing, but with a _new_ id in the url
 *   - not spoofing, but with an id in the url
 */
export function* initialize() {
  const spoofId = yield call(scrapeSpoofId);
  const auth = yield getContext('auth');
  const isSpoofing = yield call(auth.isSpoofing);
  const accountId = yield call(auth.getAccountId);
  /**
   * since the JWT lives beyond a single session, we might already be spoofing when
   * the app loads. We need to handle that lifecycle completely before we can defer
   * to the normal flow
   */
  if (isSpoofing) {
    // we're already spoofing but received a new id
    if (spoofId && spoofId !== accountId) {
      yield fork(despoof);
      yield take(SPOOF_STOPPED);
      yield put(requestSpoof(spoofId));
      yield take([SPOOF_STARTED, SPOOF_ERRORED]);
    } else {
      // we're already spoofing and keeping the same id
      yield put(startSpoof(accountId));
      yield spawn(function* listener() {
        yield take(SPOOF_CANCELLED);
        yield fork(despoof);
      });
    }
  } else if (spoofId) {
    // don't try to spoof if spoof id matches the accountId
    if (spoofId === accountId) return;
    // not spoofing, but there is an id in the url
    yield put(requestSpoof(spoofId));
  }
}

/**
 * the main spoof process watcher
 */
export default function* spoofing() {
  while (true) {
    const { payload: id } = yield take(SPOOF_REQUESTED);
    yield fork(spoof, id);

    const taken = yield take([SPOOF_ERRORED, SPOOF_CANCELLED]);
    if (taken.type === SPOOF_CANCELLED) {
      yield fork(despoof);
    }
  }
}
